import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { ChevronRight, HelpCircle, AlertTriangle, Briefcase, GraduationCap, FileCheck } from "lucide-react";
import { motion } from "framer-motion";

const guides = [
  {
    icon: HelpCircle,
    title: "What can I do after 12th for free?",
    description: "Complete guide to free education and skill options after completing higher secondary.",
    slug: "after-12th",
    color: "from-blue-500/20 to-blue-600/5",
  },
  {
    icon: Briefcase,
    title: "Understanding Apprenticeships",
    description: "Earn while you learn - everything about NAPS and apprenticeship programs.",
    slug: "apprenticeships",
    color: "from-green-500/20 to-green-600/5",
  },
  {
    icon: AlertTriangle,
    title: "How to Avoid Fake Courses & Scams",
    description: "Red flags to watch for and how to verify if a program is legitimate.",
    slug: "avoid-scams",
    color: "from-red-500/20 to-red-600/5",
  },
  {
    icon: FileCheck,
    title: "How to Apply Step-by-Step",
    description: "A simple guide to applying for free government and online programs.",
    slug: "how-to-apply",
    color: "from-purple-500/20 to-purple-600/5",
  },
  {
    icon: GraduationCap,
    title: "Skill Training vs Apprenticeship",
    description: "Understand the difference and choose what's right for you.",
    slug: "skill-vs-apprenticeship",
    color: "from-orange-500/20 to-orange-600/5",
  },
];

const Guidance = () => {
  return (
    <>
    <Seo
      title="What to Do After 12th for Free — Complete Guide | we-help.in"
      description="Free, plain-English guides on choosing free courses, apprenticeships, and avoiding scams after 12th."
      path="/guidance"
    />
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-gradient-to-b from-accent/50 to-background py-12 md:py-16">
          <div className="container">
            <div className="max-w-2xl">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Guidance & Resources
              </h1>
              <p className="text-lg text-muted-foreground">
                Simple, clear explanations to help you navigate your options after 12th. 
                No jargon, just practical guidance.
              </p>
            </div>
          </div>
        </section>

        {/* Guides List */}
        <section className="py-12">
          <div className="container">
            <div className="grid gap-4">
              {guides.map((guide, index) => (
                <motion.div
                  key={guide.slug}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/guidance/${guide.slug}`}
                    className={`flex items-center gap-4 p-6 rounded-2xl bg-gradient-to-r ${guide.color} border border-border hover:shadow-card hover:translate-x-1 transition-all group`}
                  >
                    <div className="w-14 h-14 rounded-xl bg-card flex items-center justify-center shadow-soft shrink-0">
                      <guide.icon className="h-7 w-7 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-foreground mb-1">{guide.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">{guide.description}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Quick Tips */}
        <section className="py-12 bg-muted/30">
          <div className="container">
            <h2 className="text-2xl font-bold text-foreground mb-6">Quick Tips</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Always check the official website before applying",
                "Never pay for job placements or registrations",
                "Government programs are usually free - verify at source",
                "Keep your documents ready: Aadhaar, 10th & 12th marksheets",
                "Create an email ID specifically for applications",
                "Screenshot important dates and deadlines",
              ].map((tip, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold shrink-0">
                    {index + 1}
                  </span>
                  <p className="text-sm text-foreground">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
    </>
  );
};

export default Guidance;
